import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useMasters, nameOf, sizeOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Chip, Input } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { CheckSquare, Square, FilePdf, Printer, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const WORKSHOPS = ["AMSKY", "CRON", "KODAK"];

export default function Challans() {
  const { user } = useAuth();
  const m = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = (user?.assigned_workshop || "AMSKY").toUpperCase();
  const [pending, setPending] = useState([]);
  const [challans, setChallans] = useState([]);
  const [selected, setSelected] = useState(new Set());          // pending jobs (for challan creation)
  const [selChallans, setSelChallans] = useState(new Set());    // saved challans (for bulk print)
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [partySearch, setPartySearch] = useState("");
  const [workshopFilter, setWorkshopFilter] = useState(canManageAllWorkshops ? "" : assignedWorkshop);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const pendingParams = { unchallaned: true };
    if (workshopFilter) pendingParams.workshop = workshopFilter;
    const [p, c] = await Promise.all([
      api.get("/jobs", { params: pendingParams }),
      api.get("/challans"),
    ]);
    setPending(p.data); setChallans(c.data);
  }, [workshopFilter]);
  useEffect(() => { load(); }, [load]);

  const selectedJobs = pending.filter(j => selected.has(j.id));
  const lockedClientId = selectedJobs[0]?.client_id || null;
  const lockedWorkshop = (selectedJobs[0]?.workshop || "AMSKY").toUpperCase();
  const canModifyWorkshop = (workshop) => canManageAllWorkshops || (workshop || "AMSKY").toUpperCase() === assignedWorkshop;

  const openPrintWindow = (url) => {
    const w = window.open(url, "_blank");
    if (w) {
      w.addEventListener("load", () => {
        try { w.print(); } catch (err) { /* user can print manually */ }
      });
    }
  };

  const toggle = (job) => {
    if (!canModifyWorkshop(job.workshop)) {
      toast.error(`${job.workshop || "AMSKY"} is view only. You can generate challans only for ${assignedWorkshop}.`);
      return;
    }
    const next = new Set(selected);
    if (next.has(job.id)) next.delete(job.id);
    else {
      if (selectedJobs.length > 0 && (job.workshop || "AMSKY").toUpperCase() !== lockedWorkshop) {
        toast.error(`Different workshop. A challan cannot combine ${lockedWorkshop} with ${job.workshop || "AMSKY"}.`);
        return;
      }
      if (lockedClientId && job.client_id !== lockedClientId) {
        toast.error("Different client. All jobs in a challan must be the same client.");
        return;
      }
      next.add(job.id);
    }
    setSelected(next);
  };

  const generate = async () => {
    if (selected.size === 0) return toast.error("Select at least one job");
    try {
      const { data } = await api.post("/challans", { job_ids: [...selected], remarks });
      toast.success(`Challan ${data.challan_number} created`);
      setSelected(new Set()); setRemarks("");
      load();
      openPrintWindow(`${API}/challans/${data.id}/preprinted_pdf`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelChallan = async (id, no) => {
    const reason = window.prompt(`Cancel challan ${no}? Reason:`);
    if (!reason) return;
    try { await api.post(`/challans/${id}/cancel`, { reason, release_jobs: true }); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Bulk print (multiple challans of the same client into ONE PDF) ---
  const selChallansArr = challans.filter(c => selChallans.has(c.id));
  const bulkLockedClient = selChallansArr[0]?.client_id || null;

  const toggleChallan = (c) => {
    if (c.cancelled) return;
    const next = new Set(selChallans);
    if (next.has(c.id)) next.delete(c.id);
    else {
      if (bulkLockedClient && c.client_id !== bulkLockedClient) {
        toast.error("All selected challans must belong to the same client.");
        return;
      }
      next.add(c.id);
    }
    setSelChallans(next);
  };

  const printSelectedChallans = async () => {
    if (selChallans.size === 0) return;
    setBusy(true);
    try {
      const res = await api.post("/challans/print_bulk_preprinted",
        { challan_ids: [...selChallans] },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      // Auto-trigger print once loaded
      if (w) { w.addEventListener("load", () => { try { w.print(); } catch (err) { /* user can print manually */ } }); }
      toast.success(`Opened ${selChallans.size} challans for printing.`);
      setSelChallans(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const printOne = (id) => {
    openPrintWindow(`${API}/challans/${id}/preprinted_pdf`);
  };

  const totalPlates = selectedJobs.reduce((s, j) => s + j.final_plate_qty, 0);
  const partyQuery = partySearch.trim().toLowerCase();
  const inDateRange = date => (!dateFrom || date >= dateFrom) && (!dateTo || date <= dateTo);
  const pendingView = pending.filter(job =>
    inDateRange(job.job_date) &&
    (!workshopFilter || (job.workshop || "AMSKY").toUpperCase() === workshopFilter) &&
    (!partyQuery || nameOf(m.clients, job.client_id).toLowerCase().includes(partyQuery))
  );
  const challansView = challans.filter(challan =>
    inDateRange(challan.challan_date) &&
    (!workshopFilter || (challan.workshop || "AMSKY").toUpperCase() === workshopFilter) &&
    (!partyQuery || (challan.client_name || nameOf(m.clients, challan.client_id)).toLowerCase().includes(partyQuery))
  );
  const hasFilters = Boolean(partyQuery || workshopFilter || dateFrom || dateTo);

  const updatePartySearch = (value) => {
    setPartySearch(value);
    setSelected(new Set());
    setSelChallans(new Set());
  };
  const clearFilters = () => {
    setPartySearch("");
    setWorkshopFilter(canManageAllWorkshops ? "" : assignedWorkshop);
    setDateFrom("");
    setDateTo("");
    setSelected(new Set());
    setSelChallans(new Set());
  };

  return (
    <div data-testid="challans-page">
      <Toaster position="bottom-right" />
      <PageHeader title="Challan Generation" subtitle="Print black variable data on the existing 180 × 215 mm challan stock. Every print produces Original, Duplicate and Triplicate copies; the colour PDF remains available for digital sharing." />
      <div className="px-3 pt-3 sm:px-6 sm:pt-6">
        <Card>
          <div className="p-3 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 min-w-0 w-full sm:min-w-[260px] sm:flex-1">
            <MagnifyingGlass size={18} className="text-zinc-500 shrink-0" />
            <Input value={partySearch} onChange={e => updatePartySearch(e.target.value)} placeholder="Search client name" className="w-full" data-testid="challan-party-search" />
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Workshop</div>
            <select
              value={workshopFilter}
              onChange={e => { setWorkshopFilter(e.target.value); setSelected(new Set()); setSelChallans(new Set()); }}
              className="border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm bg-white min-w-[130px]"
              data-testid="challan-workshop-filter"
            >
              <option value="">All</option>
              {WORKSHOPS.map(workshop => <option key={workshop} value={workshop}>{workshop}</option>)}
            </select>
          </label>
          {!canManageAllWorkshops && workshopFilter && workshopFilter !== assignedWorkshop && <Chip tone="warn">View only</Chip>}
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">From date</div>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setSelected(new Set()); setSelChallans(new Set()); }} data-testid="challan-date-from" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">To date</div>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setSelected(new Set()); setSelChallans(new Set()); }} data-testid="challan-date-to" />
          </label>
          {hasFilters && <Btn variant="outline" onClick={clearFilters}>Clear filters</Btn>}
          {hasFilters && <Chip tone="info">{pendingView.length} pending / {challansView.length} generated</Chip>}
        </div>
      </Card>
      </div>
      <div className="p-3 sm:p-6 grid grid-cols-1 lg:grid-cols-5 gap-4 min-w-0">
        <div className="lg:col-span-3">
          <Card>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-heading font-bold text-sm uppercase tracking-wider">Pending Jobs ({pendingView.length}{hasFilters ? ` of ${pending.length}` : ""})</div>
              {lockedClientId && <Chip tone="info">Locked: {nameOf(m.clients, lockedClientId)}</Chip>}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] data-table" data-testid="challan-pending-table">
              <thead><tr>
                <th className="px-3 py-2"></th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Client Name</th>
              <th className="px-3 py-2 text-left">Workshop</th>
              <th className="px-3 py-2 text-left">File Name</th>
              <th className="px-3 py-2 text-left">Job No.</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-right">Qty</th>
            </tr></thead>
              <tbody>
                {pendingView.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-sm">{hasFilters ? "No pending jobs match the filters." : "No pending jobs."}</td></tr>}
                {pendingView.map(j => {
                  const isSelected = selected.has(j.id);
                  const viewOnly = !canModifyWorkshop(j.workshop);
                  const clientLocked = lockedClientId && j.client_id !== lockedClientId && !isSelected;
                  const workshopLocked = selectedJobs.length > 0 && (j.workshop || "AMSKY").toUpperCase() !== lockedWorkshop && !isSelected;
                  const disabled = viewOnly || clientLocked || workshopLocked;
                  return (
                    <tr key={j.id} className={clientLocked || workshopLocked ? "opacity-40" : ""} data-testid={`challan-pending-row-${j.id}`}>
                      <td className="px-3 py-2 w-8">
                        <button onClick={() => !disabled && toggle(j)} disabled={disabled} data-testid={`challan-check-${j.id}`}>
                          {isSelected ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono">{j.job_date}</td>
                <td className="px-3 py-2 font-semibold">{nameOf(m.clients, j.client_id)}</td>
                <td className="px-3 py-2 font-mono">{j.workshop || "AMSKY"}{viewOnly && <div className="text-[10px] text-amber-700 font-sans font-semibold">View only</div>}</td>
                <td className="px-3 py-2"><div className="font-medium">{j.file_name || j.job_name}</div>{j.file_name && j.job_name && <div className="text-[11px] text-zinc-500">{j.job_name}{j.baking ? " (Baking)" : ""}</div>}</td>
                <td className="px-3 py-2 font-mono font-semibold">{j.job_number}</td>
                <td className="px-3 py-2 font-mono">{sizeOf(m.plate_sizes, j.plate_size_id)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{j.final_plate_qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card testid="challan-preview">
            <div className="px-4 py-3 border-b border-zinc-200 font-heading font-bold text-sm uppercase tracking-wider">Preview</div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Client</span><span className="font-semibold">{lockedClientId ? nameOf(m.clients, lockedClientId) : "—"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Workshop</span><span className="font-mono font-semibold">{selectedJobs.length ? lockedWorkshop : "—"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Jobs</span><span className="font-mono font-bold">{selected.size}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Total Plates</span><span className="font-mono font-bold text-lg">{totalPlates}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Print copies</span><span className="font-semibold">Original · Duplicate · Triplicate</span></div>
              <label className="block">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Remarks</div>
                <textarea value={remarks} onChange={e=>setRemarks(e.target.value)} rows={3} className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm" data-testid="challan-remarks-input" />
              </label>
              <Btn variant="primary" onClick={generate} disabled={selected.size === 0} data-testid="challan-generate-btn" className="w-full">Generate & Print Challan</Btn>
            </div>
          </Card>

          <Card className="mt-4">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-heading font-bold text-sm uppercase tracking-wider">Recent Challans ({challansView.length}{hasFilters ? ` of ${challans.length}` : ""})</div>
              {selChallans.size > 0 && (
                <Btn variant="primary" onClick={printSelectedChallans} disabled={busy} data-testid="challan-bulk-print">
                  <Printer size={14} /> Print {selChallans.size} Selected
                </Btn>
              )}
            </div>
            {selChallans.size > 0 && (
              <div className="px-4 py-2 text-[11px] text-zinc-600 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                <span>Selected: <span className="font-semibold">{nameOf(m.clients, bulkLockedClient)}</span> — {selChallans.size} challans</span>
                <button onClick={()=>setSelChallans(new Set())} className="text-sky-700 font-semibold hover:underline" data-testid="challan-bulk-clear">Clear</button>
              </div>
            )}
            <div className="max-h-96 overflow-auto">
            <table className="w-full min-w-[840px] data-table" data-testid="recent-challans-table">
              <thead><tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Client Name</th>
                <th className="px-3 py-2 text-left">Workshop</th>
                <th className="px-3 py-2 text-left">File Name</th>
                <th className="px-3 py-2 text-left">Challan</th>
                <th className="px-3 py-2 text-right">Plates</th>
                <th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {challansView.length === 0 && <tr><td colSpan={7} className="p-5 text-center text-sm text-zinc-500">{hasFilters ? "No generated challans match the filters." : "None yet."}</td></tr>}
                {challansView.map(c => {
                  const isSel = selChallans.has(c.id);
                  const disabled = c.cancelled || (bulkLockedClient && c.client_id !== bulkLockedClient && !isSel);
                  return (
                    <tr key={c.id} className={disabled ? "opacity-40" : ""} data-testid={"challan-row-" + c.id}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button onClick={() => toggleChallan(c)} disabled={disabled} className="mr-2 align-middle" data-testid={"challan-select-" + c.id}>{isSel ? <CheckSquare size={16} weight="fill" /> : <Square size={16} />}</button>
                        <span className="font-mono">{c.challan_date}</span>
                      </td>
                      <td className="px-3 py-2 font-semibold">{c.client_name || nameOf(m.clients, c.client_id)}</td>
                      <td className="px-3 py-2 font-mono">{c.workshop || "AMSKY"}{!canModifyWorkshop(c.workshop) && <div className="text-[10px] text-amber-700 font-sans font-semibold">View only</div>}</td>
                      <td className="px-3 py-2 max-w-[240px]"><div className="truncate" title={(c.file_names || []).join(", ")}>{(c.file_names || []).join(", ") || "-"}</div></td>
                      <td className="px-3 py-2 font-mono font-bold">{c.challan_number} {c.cancelled && <Chip tone="bad">Cancelled</Chip>}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.total_plates}</td>
                      <td className="px-3 py-2"><div className="flex gap-1 justify-end whitespace-nowrap">
                        <button onClick={() => printOne(c.id)} className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Print Original, Duplicate and Triplicate on 180 × 215 mm stock" data-testid={"challan-print-" + c.id}><Printer size={16} /></button>
                        <a href={API + "/challans/" + c.id + "/pdf"} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-zinc-100 rounded-sm" title="View PDF" data-testid={"challan-pdf-" + c.id}><FilePdf size={16} /></a>
                        {!c.cancelled && canModifyWorkshop(c.workshop) && <Btn variant="ghost" onClick={() => cancelChallan(c.id, c.challan_number)}>Cancel</Btn>}
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}
